/**
 * Firebase Error Suppressor
 * Handles global Firebase errors gracefully
 */

import { logHelpfulError, isPermissionError } from './firebaseIndexHelper.js';

/**
 * Initialize Firebase error suppression
 * This helps handle permission errors from snapshot listeners gracefully
 */
export const initializeFirebaseErrorSuppression = () => {
  // Override console.error to catch Firebase snapshot listener errors
  const originalConsoleError = console.error;
  
  console.error = (...args) => {
    const message = args.join(' ');
    
    // Check if this is a Firebase snapshot listener permission error
    if (message.includes('Uncaught Error in snapshot listener') && 
        message.includes('permission-denied')) {
      
      // Log a helpful message instead of the raw error
      console.group('🔒 Firebase Permission Notice');
      console.warn('Some real-time features are limited due to permission restrictions.');
      console.info('This is normal for certain user roles and does not affect core functionality.');
      console.info('Contact your administrator if you need access to additional features.');
      console.groupEnd();
      
      return; // Don't show the raw error
    }
    
    // Check if this is a Firebase write channel error (400 Bad Request)
    if (message.includes('POST https://firestore.googleapis.com') && 
        (message.includes('400 (Bad Request)') || message.includes('TYPE=terminate'))) {
      
      // Log a helpful message instead of the raw error
      console.group('🔒 Firebase Write Permission Notice');
      console.warn('Some automation features are limited due to write permission restrictions.');
      console.info('Core functionality continues to work normally.');
      console.info('Contact your administrator if you need access to automation features.');
      console.groupEnd();
      
      return; // Don't show the raw error
    }
    
    // Check if this is a Firebase index error
    if (message.includes('Firestore') && message.includes('index')) {
      console.group('🔍 Firebase Index Notice');
      console.warn('Some advanced features require database indexes.');
      console.info('The system will continue with core functionality.');
      console.info('Contact your administrator to enable all features.');
      console.groupEnd();
      
      return; // Don't show the raw error
    }
    
    // Check for other Firebase-related errors that should be suppressed
    if (message.includes('webchannel_blob_es2018.js') || 
        message.includes('persistent_stream.ts') ||
        message.includes('remote_store.ts')) {
      
      // These are internal Firebase errors that users don't need to see
      console.group('🔧 Firebase Internal Notice');
      console.info('Firebase is handling internal connection management.');
      console.info('This does not affect application functionality.');
      console.groupEnd();
      
      return; // Don't show the raw error
    }
    
    // For all other errors, use the original console.error
    originalConsoleError.apply(console, args);
  };
  
  // Also handle unhandled promise rejections from Firebase
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    
    if (error && error.message && isPermissionError(error)) {
      // Prevent the error from showing in console
      event.preventDefault();
      
      // Log helpful message
      logHelpfulError(error, 'Firebase Real-time Listener');
    }
    
    // Handle Firebase write errors
    if (error && error.message && 
        (error.message.includes('permission-denied') || 
         error.message.includes('Missing or insufficient permissions'))) {
      
      // Prevent the error from showing in console
      event.preventDefault();
      
      // Log helpful message
      console.group('🔒 Firebase Write Permission Notice');
      console.warn('Write operation blocked due to permission restrictions.');
      console.info('This is expected behavior for certain user roles.');
      console.info('Core functionality continues to work normally.');
      console.groupEnd();
    }
  });
  
  // Suppress specific Firebase network errors
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    try {
      return await originalFetch.apply(window, args);
    } catch (error) {
      // Check if this is a Firebase-related network error
      if (args[0] && typeof args[0] === 'string' && 
          args[0].includes('firestore.googleapis.com')) {
        
        // Log helpful message instead of throwing
        console.group('🌐 Firebase Network Notice');
        console.info('Firebase is managing network connections automatically.');
        console.info('This does not affect application functionality.');
        console.groupEnd();
        
        // Return a mock response to prevent further errors
        return new Response('{}', { status: 200 });
      }
      
      // For non-Firebase errors, re-throw
      throw error;
    }
  };
};

/**
 * Restore original error handling (for testing or debugging)
 */
export const restoreOriginalErrorHandling = () => {
  // This would restore the original console.error if needed
  // Implementation depends on how you want to handle this
}; 